import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Checkbox } from "../ui/checkbox";
import { Section, NativeSelect, MarginBadge, Empty } from "../common";
import { useT } from "../../lib/i18n";
import { money } from "../../lib/format";

const TYPES = ["labor", "travel", "expense", "service", "parts"];
const uid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Date.now() + Math.random()));

function convert(value, from, to, rate) {
  if (from === to || !rate) return value;
  return from === "USD" ? value * rate : value / rate;
}

const MachinePicker = ({ value, machines, onChange, disabled, testid }) => {
  const sel = new Set(value || []);
  const label = machines.filter((m) => sel.has(m.id)).map((m) => m.model).join(", ") || "—";
  return (
    <Popover>
      <PopoverTrigger asChild disabled={disabled}>
        <button data-testid={testid} className="cell-input max-w-[160px] truncate text-left">{label}</button>
      </PopoverTrigger>
      <PopoverContent className="w-64 space-y-2 p-3">
        {machines.map((m) => (
          <label key={m.id} className="flex items-center gap-2 text-xs">
            <Checkbox checked={sel.has(m.id)} onCheckedChange={(c) => { const s = new Set(sel); c ? s.add(m.id) : s.delete(m.id); onChange([...s]); }} />
            {m.model} <span className="text-neutral-400">#{m.serial_number}</span>
          </label>
        ))}
      </PopoverContent>
    </Popover>
  );
};

const Num = ({ value, onChange, disabled, testid, w = "w-20" }) => (
  <input data-testid={testid} type="number" step="any" min="0" disabled={disabled} value={value ?? ""} className={`cell-input num ${w}`}
    onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))} />
);

export default function LinesTab({ q, d, set, m, locked, showCost }) {
  const { t, lang } = useT();
  const [svc, setSvc] = useState("");
  const lines = d.lines || [];
  const calcById = Object.fromEntries((q.calc.lines || []).map((c) => [c.id, c]));
  const machines = (m.machines || []).filter((x) => (d.machine_ids || []).includes(x.id));
  const pl = (m.priceLists || []).find((p) => p.id === d.price_list_id);
  const upd = (i, patch) => set({ lines: lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) });
  const addService = () => {
    const s = (m.services || []).find((x) => x.id === svc);
    if (!s) return;
    const item = pl?.items?.find((i) => i.service_id === s.id);
    const cur = item ? pl.currency : s.currency;
    const cost = convert(item ? item.unit_cost : s.default_cost, cur, d.currency, d.exchange_rate);
    const price = convert(item ? item.unit_price : s.default_price, cur, d.currency, d.exchange_rate);
    set({ lines: [...lines, { id: uid(), line_type: s.line_type, service_id: s.id, service_code: s.code, description: lang === "en" && s.name_en ? s.name_en : s.name,
      machine_ids: d.machine_ids?.length === 1 ? [...d.machine_ids] : [], qty: 1, unit: s.unit, days: s.line_type === "labor" ? 1 : 0, overtime_hours: 0,
      unit_cost: Math.round(cost * 100) / 100, unit_price: Math.round(price * 100) / 100, discount_pct: 0, taxable: true }] });
    setSvc("");
  };
  const addManual = () => set({ lines: [...lines, { id: uid(), line_type: "service", description: "", machine_ids: [], qty: 1, unit: "unit", days: 0, overtime_hours: 0, unit_cost: 0, unit_price: 0, discount_pct: 0, taxable: true }] });
  const lr = q.calc.labor_rule;
  return (
    <Section title={t("ws.costs")} testid="section-lines" right={
      <span className="text-[11px] text-neutral-500" data-testid="labor-rule-info">{t("ws.laborRule")} ({q.country}): <b>{lr.hours_per_day} {t("ws.hpd")}</b> · <b>×{lr.overtime_factor}</b> {t("ws.otf")} · {t("ws.minMargin")} <b>{lr.min_margin_pct}%</b></span>}>
      {!locked && (
        <div className="mb-4 flex flex-wrap items-end gap-2">
          <div className="w-full sm:w-64"><NativeSelect testid="price-list-select" value={d.price_list_id} placeholder={`${t("ws.priceList")}: —`} onChange={(v) => set({ price_list_id: v })}
            options={(m.priceLists || []).map((p) => ({ value: p.id, label: `${p.name} (${p.currency})` }))} /></div>
          <div className="w-full sm:w-72"><NativeSelect testid="service-select" value={svc} placeholder={t("ws.selectService")} onChange={(v) => setSvc(v || "")}
            options={(m.services || []).filter((s) => s.active !== false).map((s) => ({ value: s.id, label: `${s.code} · ${lang === "en" && s.name_en ? s.name_en : s.name}` }))} /></div>
          <Button data-testid="add-service-line-btn" onClick={addService} disabled={!svc}><Plus className="h-4 w-4" />{t("ws.addFromCatalog")}</Button>
          <Button variant="outline" data-testid="add-manual-line-btn" onClick={addManual}><Plus className="h-4 w-4" />{t("ws.addManual")}</Button>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="dense-table w-full" data-testid="lines-table">
          <thead><tr>
            <th>#</th><th className="min-w-[120px]">{t("ws.type")}</th><th className="min-w-[240px]">{t("ws.desc")}</th><th>{t("ws.machines")}</th><th>{t("ws.days")} / {t("ws.ot")}</th><th>{t("ws.qty")}</th>
            {showCost && <th>{t("ws.unitCost")}</th>}<th>{t("ws.unitPrice")}</th><th>{t("ws.disc")}</th><th className="text-right">{t("ws.lineNet")}</th>
            {showCost && <th className="text-right">{t("ws.lineCost")}</th>}{showCost && <th>{t("ws.lineMargin")}</th>}<th />
          </tr></thead>
          <tbody>
            {lines.map((l, i) => {
              const c = calcById[l.id] || {};
              return (
                <tr key={l.id} data-testid={`line-row-${i}`}>
                  <td className="text-neutral-400">{i + 1}</td>
                  <td><select data-testid={`line-type-${i}`} disabled={locked} className="cell-input w-28" value={l.line_type} onChange={(e) => upd(i, { line_type: e.target.value })}>
                    {TYPES.map((x) => <option key={x} value={x}>{t(`lt.${x}`)}</option>)}</select></td>
                  <td><input data-testid={`line-desc-${i}`} disabled={locked} className={`cell-input ${!l.description ? "border-red-400" : ""}`} value={l.description || ""} onChange={(e) => upd(i, { description: e.target.value })} /></td>
                  <td><MachinePicker testid={`line-machines-${i}`} disabled={locked} value={l.machine_ids} machines={machines} onChange={(v) => upd(i, { machine_ids: v })} /></td>
                  <td className="whitespace-nowrap">{l.line_type === "labor" ? (<span className="flex gap-1">
                    <Num testid={`line-days-${i}`} disabled={locked} value={l.days} onChange={(v) => upd(i, { days: v })} w="w-14" />
                    <Num testid={`line-ot-${i}`} disabled={locked} value={l.overtime_hours} onChange={(v) => upd(i, { overtime_hours: v })} w="w-14" /></span>) : <span className="text-neutral-300">—</span>}</td>
                  <td>{l.line_type === "labor" ? <span className="num block w-16 text-neutral-600" title="h">{c.qty ?? "—"} h</span>
                    : <Num testid={`line-qty-${i}`} disabled={locked} value={l.qty} onChange={(v) => upd(i, { qty: v })} w="w-16" />}</td>
                  {showCost && <td><Num testid={`line-cost-${i}`} disabled={locked} value={l.unit_cost} onChange={(v) => upd(i, { unit_cost: v })} w="w-24" /></td>}
                  <td><Num testid={`line-price-${i}`} disabled={locked} value={l.unit_price} onChange={(v) => upd(i, { unit_price: v })} w="w-24" /></td>
                  <td><Num testid={`line-disc-${i}`} disabled={locked} value={l.discount_pct} onChange={(v) => upd(i, { discount_pct: v })} w="w-14" /></td>
                  <td className="num font-semibold">{money(c.net)}</td>
                  {showCost && <td className="num text-neutral-600">{money(c.cost)}</td>}
                  {showCost && <td><MarginBadge value={c.margin_pct} /></td>}
                  <td>{!locked && <button data-testid={`line-delete-${i}`} onClick={() => set({ lines: lines.filter((_, j) => j !== i) })} className="text-neutral-400 hover:text-[#db0720]"><Trash2 className="h-4 w-4" /></button>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!lines.length && <Empty>{t("ws.noLines")}</Empty>}
      </div>
      <p className="mt-3 text-[11px] text-neutral-500">{t("cat.frozenNote")}</p>
    </Section>
  );
}
